import { useState } from 'react';
import {
  ActionButton,
  Button,
  DataTable,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  HairlineCard,
  Icons,
  MenuItem,
  RowAction,
  RowActions,
  Stack,
  TagChip,
  TextField,
  titleCellSx,
  Typography,
  useRowActionsColumn,
} from '@munifocus/ui';
import { useTranslation } from 'react-i18next';
import { InviteLanguageField, type InviteLanguage } from './InviteLanguageField';
import { LG_CONTACT_ROLES, type LgContactRole } from '@munifocus/contracts-gov';
import { useAddLgContact, useLgContacts, useRemoveLgContact } from '../../lib/administration-api';
import { useScope } from '../../providers/ScopeProvider';
import { useMutationToast } from '../../lib/mutation-toast';
import { useConfirmAction } from '../../lib/useConfirmAction';
import { useSubmitValidation } from '../../lib/useSubmitValidation';

/**
 * The people at each municipality this contractor serves on the limited
 * licence.
 *
 * Such a municipality has no Munifocus licence and nobody at Munifocus setting
 * it up, so the contractor that brought it in invites its people: the clerk who
 * approves the contract and the officers under it, the solicitor, and
 * read-only observers. As many as the municipality needs. The company can
 * remove only people it added — the municipality's own clerk is not its to
 * take away.
 */
export function LgContactsPanel() {
  const { t } = useTranslation();
  const { active } = useScope();
  const toast = useMutationToast();
  const confirm = useConfirmAction();
  const actionsColumn = useRowActionsColumn();
  const companyId = active?.kind === 'company' ? active.id : null;

  const contacts = useLgContacts(companyId);
  const add = useAddLgContact(companyId ?? '');
  const remove = useRemoveLgContact(companyId ?? '');

  const [adding, setAdding] = useState(false);
  const [lgId, setLgId] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<LgContactRole>('clerk');
  const [language, setLanguage] = useState<InviteLanguage>('en');

  const v = useSubmitValidation({
    lgId: lgId ? null : t('common.validation.choose'),
    email: !email.trim()
      ? t('common.validation.required')
      : /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
        ? null
        : t('common.validation.invalidEmail'),
  });

  const close = () => {
    v.reset();
    setAdding(false);
    setEmail('');
    setName('');
    setRole('clerk');
    setLanguage('en');
  };

  if (!companyId) {
    return (
      <HairlineCard sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary">
          {t('gov.admin.lgContacts.pickCompany')}
        </Typography>
      </HairlineCard>
    );
  }

  const governments = contacts.data ?? [];

  return (
    <Stack spacing={2}>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '68ch' }}>
          {t('gov.admin.lgContacts.help')}
        </Typography>
        {governments.length > 0 && (
          <ActionButton
            label={t('gov.admin.lgContacts.add')}
            icon={Icons.UserPlus}
            onClick={() => {
              setLgId(governments[0]?.localGovernmentId ?? '');
              setAdding(true);
            }}
          />
        )}
      </Stack>

      {!contacts.isLoading && governments.length === 0 && (
        <HairlineCard sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary">
            {t('gov.admin.lgContacts.none')}
          </Typography>
        </HairlineCard>
      )}

      {governments.map((lg) => (
        <HairlineCard key={lg.localGovernmentId} sx={{ p: 2.5 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
            {lg.localGovernmentName}
          </Typography>
          <DataTable
            rows={lg.members}
            getRowKey={(row) => row.id}
            emptyLabel={t('gov.admin.lgContacts.emptyAt', { place: lg.localGovernmentName })}
            initialSortKey="name"
            columns={[
              {
                key: 'name',
                label: t('gov.admin.members.name'),
                primary: true,
                sortValue: (row) => row.userName,
                cell: (row) => (
                  <Stack>
                    <Typography variant="body2" sx={titleCellSx}>
                      {row.userName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {row.userEmail}
                    </Typography>
                  </Stack>
                ),
              },
              {
                key: 'role',
                label: t('gov.admin.members.role'),
                sortValue: (row) => row.role,
                cell: (row) => <TagChip label={t(`gov.admin.roles.${row.role}`)} />,
              },
              {
                ...actionsColumn,
                cell: (row) =>
                  !lg.removableIds.includes(row.id) ? null : (
                    <RowActions>
                      <RowAction
                        label={t('gov.admin.members.remove')}
                        icon={Icons.Trash2}
                        tone="danger"
                        onClick={async () => {
                          if (
                            !(await confirm({
                              title: t('gov.admin.members.removeTitle'),
                              body: t('gov.admin.members.removeBody', {
                                name: row.userName,
                                role: t(`gov.admin.roles.${row.role}`),
                              }),
                              confirmLabel: t('gov.admin.members.remove'),
                              tone: 'danger',
                            }))
                          ) {
                            return;
                          }
                          try {
                            await remove.mutateAsync(row.id);
                            toast.success('gov.admin.members.removed');
                          } catch (err) {
                            toast.error(err, 'gov.admin.errors.actionFailed');
                          }
                        }}
                      />
                    </RowActions>
                  ),
              },
            ]}
          />
        </HairlineCard>
      ))}

      <Dialog open={adding} onClose={close} fullWidth maxWidth="sm">
        <DialogTitle>{t('gov.admin.lgContacts.add')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              select
              label={t('gov.admin.lgContacts.localGovernment')}
              {...v.field('lgId')}
              value={lgId}
              onChange={(e) => setLgId(e.target.value)}
              fullWidth
            >
              {governments.map((lg) => (
                <MenuItem key={lg.localGovernmentId} value={lg.localGovernmentId}>
                  {lg.localGovernmentName}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label={t('gov.admin.members.email')}
              {...v.field('email')}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              fullWidth
              helperText={v.errors.email ?? v.errors.lgId ?? t('gov.admin.members.emailHelp')}
            />
            <TextField
              label={t('gov.admin.members.name')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
            />
            <TextField
              select
              label={t('gov.admin.members.role')}
              value={role}
              onChange={(e) => setRole(e.target.value as LgContactRole)}
              helperText={t(`gov.admin.roleAccess.${role}`)}
              fullWidth
            >
              {LG_CONTACT_ROLES.map((r) => (
                <MenuItem key={r} value={r}>
                  {t(`gov.admin.roles.${r}`)}
                </MenuItem>
              ))}
            </TextField>
            <InviteLanguageField value={language} onChange={setLanguage} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>{t('common.actions.cancel')}</Button>
          <Button
            variant="contained"
            disabled={add.isPending}
            onClick={v.guard(async () => {
              try {
                await add.mutateAsync({
                  localGovernmentId: lgId,
                  email: email.trim(),
                  displayName: name.trim() || undefined,
                  role,
                  language,
                });
                toast.success('gov.admin.lgContacts.added');
                close();
              } catch (err) {
                toast.error(err, 'gov.admin.errors.actionFailed');
              }
            })}
          >
            {t('gov.admin.lgContacts.invite')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
